import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import XanoAPI from "../../lib/XanoAPI";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/use-toast";
import { Trash2, AlertTriangle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

interface MassDeleteProps {
  selectedIds: string[];
  onSuccess?: () => void;
}

export function MassDelete({ selectedIds, onSuccess }: MassDeleteProps) {
  const api = XanoAPI.getInstance();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setIsDeleting(true);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
      
      // Process deletions
      for (const id of selectedIds) {
        try {
          await api.deleteClient(id);
          successCount++;
        } catch (error) {
          errorCount++;
          errors.push(id);
          console.error(`Failed to delete client ${id}:`, error);
        }
      }
      
      // Close dialog
      setIsOpen(false);
      
      // Show results
      if (errorCount === 0) {
        toast({
          title: "Clients Deleted Successfully",
          description: `Successfully deleted ${successCount} client${successCount > 1 ? 's' : ''}.`,
        });
      } else {
        toast({
          title: "Partial Success",
          description: `Deleted ${successCount} of ${selectedIds.length} client${selectedIds.length > 1 ? 's' : ''}. ${errorCount} failed.`,
          variant: "destructive",
        });
      }
      
      // Refresh table data
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      
      // Call success callback
      onSuccess?.();
      
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "An error occurred while deleting clients. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button 
          variant="destructive"
          size="sm"
          disabled={selectedIds.length === 0 || isDeleting}
        >
          {isDeleting ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="h-4 w-4 mr-2" />
              {selectedIds.length > 0 ? `Mass Delete (${selectedIds.length})` : 'Mass Delete'}
            </>
          )}
        </Button>
      </AlertDialogTrigger>
      
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Delete {selectedIds.length} Client{selectedIds.length > 1 ? 's' : ''}?
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              This action cannot be undone. This will permanently delete the
              selected client{selectedIds.length > 1 ? 's' : ''} and remove all their data
              from the system.
            </p>
            {selectedIds.length > 1 && (
              <p className="font-semibold text-red-600">
                You are about to delete {selectedIds.length} clients permanently.
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Deleting...
              </>
            ) : (
              <>Yes, Delete {selectedIds.length > 1 ? 'All' : ''}</>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
